import React, { useState, useEffect, useRef } from 'react';
import AppHeader from '../components/AppHeader';
import MiniQuiz from '../components/MiniQuiz';
import { MODULES } from '../data/content';

// ── DÉMO IA ANIMÉE ──
const DEMO_COURRIER_FR = "Madame, Monsieur,\nNous vous informons que votre dossier CAF n° 2847156 est suspendu. Vous devez fournir vos justificatifs de revenus avant le 18 février 2026, faute de quoi vos allocations seront interrompues.";
const DEMO_REPONSE_FR = "📋 La CAF suspend vos aides\n\n✅ Ce qu'il faut faire :\n1. Rassemblez vos bulletins de salaire (3 derniers mois)\n2. Envoyez-les via caf.fr ou en agence\n⏰ Délai : avant le 18 février — urgent !";

function DemoIA({ scale, isMobile, onNavigate, t }) {
  const [phase, setPhase] = useState(0);
  const [typed, setTyped] = useState('');
  const [reponseVisible, setReponseVisible] = useState(false);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  const demoCourrier = t?.heroV2?.demo_courrier?.replace(/\\n/g, '\n') || DEMO_COURRIER_FR;
  const demoReponse  = t?.heroV2?.demo_reponse?.replace(/\\n/g, '\n')  || DEMO_REPONSE_FR;

  useEffect(() => {
    let i = 0;
    setPhase(1);
    setTyped('');
    setReponseVisible(false);

    intervalRef.current = setInterval(() => {
      if (i < demoCourrier.length) {
        setTyped(demoCourrier.slice(0, i + 1));
        i++;
      } else {
        clearInterval(intervalRef.current);
        setPhase(2);
        timeoutRef.current = setTimeout(() => {
          setReponseVisible(true);
          setPhase(3);
        }, 1200);
      }
    }, 22);

    return () => {
      clearInterval(intervalRef.current);
      clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <div style={{ fontFamily: 'Fraunces, serif', fontSize: (isMobile ? 17 : 20) * scale, fontWeight: 700, color: '#1A1A1A', marginBottom: 4 }}>
          {t?.heroV2?.demo_title || 'Voyez le résultat avant de commencer'}
        </div>
        <div style={{ fontSize: (isMobile ? 12 : 13) * scale, color: '#666' }}>
          {t?.heroV2?.demo_subtitle || "Voici ce que l'IA produit en 5 secondes avec un vrai courrier"}
        </div>
      </div>

      {/* Terminal macOS */}
      <div style={{
        background: '#1a1a1a', borderRadius: isMobile ? 14 : 18,
        overflow: 'hidden',
        boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
      }}>
        {/* Barre titre macOS */}
        <div style={{
          background: '#2a2a2a', padding: '10px 14px',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#FF5F57' }} />
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#FEBC2E' }} />
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#28C840' }} />
          </div>
          <div style={{ flex: 1, textAlign: 'center', fontSize: 11 * scale, color: '#666', fontFamily: 'monospace' }}>
            demarchesclaires.fr/que-faire
          </div>
        </div>

        {/* Zone courrier */}
        <div style={{
          padding: isMobile ? '14px 16px' : '18px 20px',
          fontFamily: 'monospace', fontSize: (isMobile ? 11 : 12) * scale,
          color: '#D0D0D0', lineHeight: 1.65, minHeight: 80,
          whiteSpace: 'pre-wrap',
        }}>
          {typed}
          {phase === 1 && <span style={{ borderRight: '2px solid #4ADE80', marginLeft: 1 }}> </span>}
        </div>

        {/* Analyse en cours */}
        {phase === 2 && (
          <div style={{ padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 8, color: '#888', fontSize: (isMobile ? 11 : 12) * scale, fontFamily: 'monospace' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#4ADE80', boxShadow: '0 0 6px #4ADE80' }} />
            <span>{t?.heroV2?.demo_analyse || 'Mistral analyse votre courrier'}</span>
            <span style={{ letterSpacing: 2 }}>...</span>
          </div>
        )}

        {/* Réponse IA */}
        {reponseVisible && (
          <div style={{
            margin: '0 16px 16px', padding: '14px 16px',
            background: '#0d2a0d', border: '1px solid #166534',
            borderRadius: 10,
            fontSize: (isMobile ? 12 : 13) * scale,
            color: '#4ADE80', lineHeight: 1.65,
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
          }}>
            {demoReponse}
          </div>
        )}

        {reponseVisible && (
          <div style={{ padding: '0 16px 16px' }}>
            <button
              onClick={() => onNavigate("quefaire")}
              style={{
                width: '100%', padding: isMobile ? '12px 0' : '13px 0',
                borderRadius: 10, border: 'none',
                background: '#0063CB', color: '#fff',
                fontSize: (isMobile ? 13 : 14) * scale,
                fontWeight: 700, cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#003189'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#0063CB'; }}>
              {t?.heroV2?.demo_cta || 'Analyser mon courrier →'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── WIDGET FLOTTANT ── (intouchable)
function WidgetFlottant({ scale, onNavigate }) {
  const [ouvert, setOuvert] = useState(false);
  const [isMob, setIsMob] = useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handle = () => setIsMob(window.innerWidth < 768);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  const suggestions = [
    "J'ai reçu un courrier CAF",
    "France Travail me demande un justificatif",
    "Je dois contester une décision",
    "Je veux écrire un courrier",
  ];

  return (
    <div style={{ position: "fixed", bottom: 100, right: isMob ? 16 : 24, zIndex: 9999 }}>
      {ouvert && (
        <div style={{
          position: "absolute", bottom: 64, right: 0,
          width: isMob ? "calc(100vw - 32px)" : 340,
          background: "#fff", borderRadius: 20,
          boxShadow: "0 8px 40px rgba(0,49,137,0.18)",
          border: "1.5px solid #E0E0EE",
          overflow: "hidden",
        }}>
          <div style={{ background: "#003189", padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#4ADE80" }} />
              <span style={{ color: "#fff", fontSize: 13 * scale, fontWeight: 700 }}>Posez votre question</span>
            </div>
            <button onClick={() => setOuvert(false)} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.7)", fontSize: 18, cursor: "pointer", padding: 0 }}>✕</button>
          </div>
          <div style={{ padding: "14px 16px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {suggestions.map((s, i) => (
                <button key={i}
                  onClick={() => { onNavigate("quefaire"); }}
                  style={{
                    textAlign: "left", background: "#F5F5FE",
                    border: "1px solid #E0E0EE", borderRadius: 8,
                    padding: "7px 12px", fontSize: 12 * scale,
                    color: "#003189", cursor: "pointer",
                    fontFamily: "DM Sans, sans-serif",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#E8EDFF"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#F5F5FE"; }}>
                  {s}
                </button>
              ))}
            </div>
            <button
              onClick={() => onNavigate("quefaire")}
              style={{
                marginTop: 12, width: "100%",
                padding: "11px 0", borderRadius: 10, border: "none",
                background: "#CE0500", color: "#fff",
                fontSize: 13 * scale, fontWeight: 700, cursor: "pointer",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#A80000"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#CE0500"; }}>
              🚨 Analyser maintenant
            </button>
          </div>
        </div>
      )}
      <button
        onClick={() => setOuvert(o => !o)}
        style={{
          width: 56, height: 56, borderRadius: "50%",
          background: "#CE0500", border: "none",
          boxShadow: "0 4px 20px rgba(206,5,0,0.45)",
          cursor: "pointer", fontSize: 24,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 0.18s ease",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "#A80000"; e.currentTarget.style.transform = "scale(1.08)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "#CE0500"; e.currentTarget.style.transform = "scale(1)"; }}
        title="Analyser un courrier">
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <span style={{ fontSize: 22 }}>{ouvert ? "✕" : "🤖"}</span>
          {!ouvert && <span style={{ fontSize: 9, color: "#fff", fontWeight: 700, letterSpacing: "0.05em" }}>ANALYSER</span>}
        </div>
      </button>
    </div>
  );
}

// ── FORMULAIRE CONTACT ──
function ContactForm({ scale, isMobile, t }) {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');

  const handleSend = async () => {
    if (!form.name || !form.message) {
      setMsg(t?.contact?.err || '⚠️ Veuillez renseigner votre nom et votre message.');
      return;
    }
    setSending(true); setMsg('');
    try {
      const res = await fetch('https://formspree.io/f/xgopbjgd', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ nom: form.name, email: form.email, message: form.message }),
      });
      if (res.ok) {
        setMsg(t?.contact?.ok || '✅ Message envoyé ! Nous vous répondrons rapidement.');
        setForm({ name: '', email: '', message: '' });
      } else {
        setMsg(t?.contact?.err || '❌ Erreur — réessayez ou écrivez à contact@demarchesclaires.fr');
      }
    } catch {
      setMsg(t?.contact?.err || '❌ Erreur réseau — réessayez.');
    } finally {
      setSending(false);
    }
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    padding: '10px 14px', borderRadius: 10,
    border: '1.5px solid #D0D8F0', background: '#F8F9FF',
    fontSize: 13 * scale, color: '#1A1A1A',
    outline: 'none', marginBottom: 10,
    fontFamily: 'DM Sans, sans-serif',
  };

  return (
    <div style={{
      background: '#fff', border: '1.5px solid #E0E0EE',
      borderRadius: isMobile ? 16 : 20,
      padding: isMobile ? '1.2rem' : '1.6rem',
      marginTop: 40,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: '#E8EDFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
          ✉️
        </div>
        <div>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: (isMobile ? 15 : 17) * scale, fontWeight: 700, color: '#003189' }}>
            {t?.contact?.title || 'Nous écrire'}
          </div>
          <div style={{ fontSize: (isMobile ? 11 : 12) * scale, color: '#666', marginTop: 2 }}>
            {t?.contact?.sub || 'Une question ? Une suggestion ? Écrivez-nous.'}
          </div>
        </div>
      </div>

      <input type="text" placeholder={t?.contact?.name || 'Votre nom *'} value={form.name}
        onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} />
      <input type="email" placeholder={t?.contact?.email || 'Votre email (pour vous répondre)'} value={form.email}
        onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} />
      <textarea placeholder={t?.contact?.message || 'Votre message *'} value={form.message}
        onChange={e => setForm({ ...form, message: e.target.value })}
        rows={isMobile ? 4 : 5} style={{ ...inputStyle, resize: 'vertical', marginBottom: 12 }} />

      <button onClick={handleSend} disabled={sending} style={{
        width: '100%', padding: '11px 0', borderRadius: 10, border: 'none',
        background: sending ? '#AAA' : '#003189', color: '#fff',
        fontSize: 13 * scale, fontWeight: 700, cursor: sending ? 'not-allowed' : 'pointer',
        transition: 'background 0.18s',
      }}
        onMouseEnter={e => { if (!sending) e.currentTarget.style.background = '#0063CB'; }}
        onMouseLeave={e => { if (!sending) e.currentTarget.style.background = '#003189'; }}>
        {sending ? (t?.contact?.sending || 'Envoi en cours...') : (t?.contact?.send || 'Envoyer le message')}
      </button>

      {msg && (
        <div style={{
          marginTop: 10, padding: '10px 14px', borderRadius: 10,
          background: msg.startsWith('✅') ? '#F0FDF4' : '#FFF1F2',
          border: `1px solid ${msg.startsWith('✅') ? '#BBF7D0' : '#FCCACA'}`,
          fontSize: 13 * scale, color: msg.startsWith('✅') ? '#166534' : '#991B1B',
        }}>
          {msg}
        </div>
      )}
    </div>
  );
}

export default function Home({ onNavigate, large, toggleText, scale = 1, totalCompleted = 0, t, isRTL, lang, switchLang, onDiagnostic }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  const statsTitle  = t?.stats?.statsTitle || "📊 En France aujourd'hui";
  const sourceBtn   = t?.stats?.sourceBtn  || '📰 Source';
  const sectionModulesLabel = t?.modules?.sectionTitle || '📚 Tous les modules';

  const situations = [
    { icon: '📬', color: '#0063CB', bg: '#EFF6FF', module: 'courrier',
      label: t?.situations?.courrier    || "J'ai reçu un courrier officiel que je ne comprends pas",
      sub:   t?.situations?.courrierSub || 'CAF, impôts, préfecture, huissier…' },
    { icon: '📄', color: '#169B62', bg: '#F0FDF4', module: 'pdf',
      label: t?.situations?.pdf    || "On me demande de remplir ou signer un formulaire",
      sub:   t?.situations?.pdfSub || 'PDF, cerfa, document en ligne…' },
    { icon: '🏛️', color: '#7B5EA7', bg: '#FAF5FF', module: 'rsacaf',
      label: t?.situations?.caf    || "J'ai un problème avec la CAF ou mes allocations",
      sub:   t?.situations?.cafSub || "RSA, APL, prime d'activité…" },
    { icon: '💊', color: '#00A3A6', bg: '#F0FDFA', module: 'cartevitale',
      label: t?.situations?.sante    || "J'ai un souci avec ma carte vitale ou l'Assurance Maladie",
      sub:   t?.situations?.santeSub || 'CPAM, médecin traitant, remboursements…' },
    { icon: '🏠', color: '#CE0500', bg: '#FFF1F2', module: 'logement',
      label: t?.situations?.logement    || "J'ai un problème de logement ou d'APL",
      sub:   t?.situations?.logementSub || 'Aide au logement, bailleur, expulsion…' },
  ];

  const statsInsee = [
    { num: t?.stats?.stat1num || '15%', label: t?.stats?.stat1label || "de la population en situation d'illectronisme (INSEE 2023)", color: '#CE0500' },
    { num: t?.stats?.stat2num || '28%', label: t?.stats?.stat2label || 'ont des compétences numériques faibles (Crédoc 2022)',       color: '#E05D00' },
    { num: t?.stats?.stat3num || '29%', label: t?.stats?.stat3label || "n'ont fait aucune démarche dématérialisée en 12 mois (Crédoc 2022)", color: '#7B5EA7' },
  ];


  return (
    <div style={{ minHeight: '100vh', direction: isRTL ? 'rtl' : 'ltr', background: '#F5F5FE' }}>
      <AppHeader
        onNavigate={onNavigate} large={large} toggleText={toggleText}
        t={t} isRTL={isRTL} lang={lang} switchLang={switchLang} onDiagnostic={onDiagnostic}
      />

      {/* ── HERO BLANC ── */}
      <section style={{
        background: '#F8F9FF',
        padding: isMobile ? '1.8rem 1.2rem 1.8rem' : '2.5rem 2rem 2.5rem',
        textAlign: 'center',
      }}>
        {/* Badge */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: '#fff', border: '1px solid #D0D8F0',
          borderRadius: 100, padding: '5px 16px',
          fontSize: (isMobile ? 11 : 12) * scale, fontWeight: 500,
          color: '#555', marginBottom: 24,
        }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#4ADE80', boxShadow: '0 0 0 3px #4ADE8030' }} />
          {t?.heroV2?.hero_badge || 'IA · 7 langues · Gratuit'}
        </div>

        {/* Titre */}
        <h1 style={{
          fontFamily: 'Fraunces, serif',
          fontSize: isMobile ? 28 * scale : 42 * scale,
          fontWeight: 700, lineHeight: 1.12, color: '#1A1A1A',
          margin: '0 0 18px', letterSpacing: '-0.02em',
          maxWidth: 680, marginLeft: 'auto', marginRight: 'auto',
        }}>
          {(t?.heroV2?.hero_title || 'Arrêtez de subir vos\ndémarches administratives').split('\n')[0]}<br />
          <span style={{ color: '#003189' }}>{(t?.heroV2?.hero_title || 'Arrêtez de subir vos\ndémarches administratives').split('\n')[1]}</span>
        </h1>

        {/* Sous-titre */}
        <p style={{
          fontSize: (isMobile ? 14 : 16) * scale,
          color: '#555', lineHeight: 1.65,
          maxWidth: 500, margin: '0 auto 32px',
        }}>
          {t?.heroV2?.hero_subtitle || 'Collez votre courrier. Notre IA analyse et vous guide en 5 secondes.'}
        </p>

        {/* 2 boutons hero */}
        <div style={{ display: 'flex', gap: isMobile ? 8 : 12, justifyContent: 'center', marginBottom: 18 }}>
          <button
            onClick={() => onNavigate('quefaire')}
            style={{
              padding: isMobile ? '13px 22px' : '14px 32px',
              borderRadius: 12, border: 'none',
              background: '#003189', color: '#fff',
              fontSize: (isMobile ? 13 : 15) * scale,
              fontWeight: 700, cursor: 'pointer',
              fontFamily: 'DM Sans, sans-serif',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#0063CB'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#003189'; }}>
            {t?.heroV2?.hero_cta_main || '⚡ Analyser maintenant'}
          </button>
          <button
            onClick={() => onNavigate('jeexplique')}
            style={{
              padding: isMobile ? '13px 22px' : '14px 32px',
              borderRadius: 12, border: '1.5px solid #D0D8F0',
              background: '#fff', color: '#333',
              fontSize: (isMobile ? 13 : 15) * scale,
              fontWeight: 500, cursor: 'pointer',
              fontFamily: 'DM Sans, sans-serif',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#F0F4FF'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}>
            {t?.heroV2?.hero_cta_demo || 'Voir la démo en 30s'}
          </button>
        </div>

        {/* Reassurance */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: isMobile ? 12 : 24, flexWrap: 'wrap' }}>
          <span style={{ fontSize: (isMobile ? 11 : 12) * scale, color: '#888' }}>{t?.heroV2?.hero_reassurance1 || '✓ Aucune inscription'}</span>
          <span style={{ fontSize: (isMobile ? 11 : 12) * scale, color: '#888' }}>{t?.heroV2?.hero_reassurance2 || '✓ Données supprimées après analyse'}</span>
        </div>
      </section>

      {/* ── MAIN ── */}
      <main style={{ maxWidth: 860, margin: '0 auto', padding: isMobile ? '1.5rem 1rem 6rem' : '2.5rem 1.5rem 6rem' }}>

        {/* ── BOUTON QUEFAIRE (intouchable) ── */}
        <div style={{ marginBottom: 20 }}>
          <button
            onClick={() => onNavigate('quefaire')}
            style={{
              width: '100%', padding: isMobile ? '18px 20px' : '22px 28px',
              borderRadius: isMobile ? 16 : 20,
              border: 'none', background: '#CE0500',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              cursor: 'pointer', textAlign: 'left', transition: 'all 0.18s ease',
              boxShadow: '0 4px 20px rgba(206,5,0,0.25)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = '#A80000';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 28px rgba(206,5,0,0.35)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = '#CE0500';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(206,5,0,0.25)';
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{
                width: isMobile ? 48 : 56, height: isMobile ? 48 : 56,
                borderRadius: 14, background: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: isMobile ? 26 : 30, flexShrink: 0,
              }}>
                🚨
              </div>
              <div>
                <div style={{
                  fontSize: (isMobile ? 15 : 18) * scale,
                  fontWeight: 700, color: '#fff', marginBottom: 5,
                  fontFamily: 'Fraunces, serif', letterSpacing: '-0.01em',
                }}>
                  {t?.quefaire?.title || 'Un courrier vous inquiète ?'}
                </div>
                <div style={{ fontSize: (isMobile ? 12 : 13) * scale, color: 'rgba(255,255,255,0.85)', lineHeight: 1.45 }}>
                  {t?.quefaire?.sub || "On vous dit ce qu'il veut et ce que vous devez faire — en mots simples."}
                </div>
              </div>
            </div>
            <span style={{ color: '#fff', fontSize: 24, flexShrink: 0, marginLeft: 8, opacity: 0.8 }}>›</span>
          </button>
        </div>

        {/* ── DÉMO IA ANIMÉE ── */}
        <DemoIA scale={scale} isMobile={isMobile} onNavigate={onNavigate} t={t} />

        {/* ── SITUATIONS URGENCE ── */}
        <section style={{
          background: '#fff', border: '1.5px solid #FCCACA',
          borderRadius: isMobile ? 16 : 20,
          padding: isMobile ? '1.2rem' : '1.6rem',
          marginBottom: 28,
          boxShadow: '0 2px 10px rgba(206,5,0,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
              🚨
            </div>
            <div>
              <div style={{ fontFamily: 'Fraunces, serif', fontSize: (isMobile ? 15 : 17) * scale, fontWeight: 700, color: '#CE0500', marginBottom: 3 }}>
                {(t?.situations?.title || '🚨 Vous avez un problème maintenant ?').replace('🚨 ', '')}
              </div>
              <div style={{ fontSize: (isMobile ? 12 : 13) * scale, color: '#666' }}>
                {t?.situations?.subtitle || 'Cliquez sur votre situation — on vous guide directement.'}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 10 }}>
            {situations.map((sit) => (
              <div key={sit.module}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  background: '#F5F5FE', border: '1.5px solid #E0E0EE',
                  borderRadius: 14, padding: isMobile ? '12px 14px' : '14px 16px',
                  cursor: 'pointer', transition: 'all 0.18s ease',
                  textAlign: isRTL ? 'right' : 'left',
                }}
                onClick={() => onNavigate('module', sit.module)}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = sit.color;
                  e.currentTarget.style.background = sit.bg;
                  e.currentTarget.style.transform = isRTL ? 'translateX(-3px)' : 'translateX(3px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = '#E0E0EE';
                  e.currentTarget.style.background = '#F5F5FE';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}>
                <div style={{ width: isMobile ? 42 : 46, height: isMobile ? 42 : 46, borderRadius: 12, flexShrink: 0, background: sit.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: isMobile ? 20 : 22 }}>
                  {sit.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: (isMobile ? 12 : 13) * scale, fontWeight: 700, color: '#1A1A1A', marginBottom: 2, lineHeight: 1.35 }}>
                    {sit.label}
                  </div>
                  <div style={{ fontSize: (isMobile ? 10 : 11) * scale, color: '#888' }}>{sit.sub}</div>
                </div>
                <div style={{ color: sit.color, fontSize: 18, opacity: 0.7, flexShrink: 0 }}>{isRTL ? '‹' : '›'}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── MINI QUIZ ── */}
        <div style={{
          background: '#003189', borderRadius: isMobile ? 16 : 20,
          padding: isMobile ? '1.2rem' : '1.5rem', marginBottom: 28,
          boxShadow: '0 4px 20px rgba(0,49,137,0.18)',
        }}>
          <MiniQuiz onNavigate={onNavigate} scale={scale} lang={lang} />
        </div>

        {/* ── STATS INSEE ── */}
        <div style={{
          background: '#fff', border: '1px solid #E0E0EE',
          borderRadius: isMobile ? 16 : 20,
          padding: isMobile ? '1.2rem' : '1.5rem', marginBottom: 28,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            <div style={{ fontSize: (isMobile ? 10 : 11) * scale, fontWeight: 700, color: '#666', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              {statsTitle}
            </div>
            <a href="https://www.insee.fr/fr/statistiques/7636058"
              target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 12 * scale, fontWeight: 700, color: '#0063CB', background: '#E8EDFF', border: '1px solid #0063CB40', borderRadius: 20, padding: '4px 12px', textDecoration: 'none' }}>
              {sourceBtn}
            </a>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 10 }}>
            {statsInsee.map((item, i) => (
              <div key={i} style={{ background: `${item.color}08`, border: `1px solid ${item.color}25`, borderRadius: 12, padding: '14px 12px', textAlign: 'center' }}>
                <div style={{ fontFamily: 'Fraunces, serif', fontSize: (isMobile ? 22 : 24) * scale, fontWeight: 700, color: item.color }}>{item.num}</div>
                <div style={{ fontSize: (isMobile ? 10 : 11) * scale, color: '#555', marginTop: 5, lineHeight: 1.4 }}>{item.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── MODULES GRID ── */}
        <section>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: (isMobile ? 16 : 18) * scale, fontWeight: 700, color: '#1A1A1A', marginBottom: 4 }}>
            {sectionModulesLabel}
          </div>
          <p style={{ fontSize: (isMobile ? 12 : 13) * scale, color: '#666', marginBottom: 16, lineHeight: 1.5 }}>
            {t?.modules?.subtitle || 'Du plus simple au plus complexe — commencez où vous voulez.'}
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: isMobile ? 10 : 12,
          }}>
            {MODULES.map((mod) => (
              <div key={mod.id}
                style={{
                  background: '#fff', border: '1.5px solid #E0E0EE',
                  borderRadius: 16, padding: isMobile ? '14px 12px' : '16px 14px',
                  cursor: 'pointer', transition: 'all 0.18s ease',
                  display: 'flex', flexDirection: 'column', gap: 10,
                }}
                onClick={() => onNavigate('module', mod.id)}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = mod.color;
                  e.currentTarget.style.boxShadow = `0 4px 16px ${mod.color}22`;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = '#E0E0EE';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: `${mod.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
                  {mod.icon}
                </div>
                <div style={{ fontSize: (isMobile ? 12 : 13) * scale, fontWeight: 600, color: '#1A1A1A', lineHeight: 1.35 }}>
                  {t?.moduleNames?.[mod.id] || mod.title}
                </div>
                <div style={{ height: 3, background: '#F0F0F8', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 10, background: mod.color, width: '0%' }} />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── FORMULAIRE CONTACT ── */}
        <ContactForm scale={scale} isMobile={isMobile} t={t} />

        {/* ── FOOTER ── */}
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <a href="https://orchestrateur-ai.fr" target="_blank" rel="noopener noreferrer" style={{ display: 'none' }}>🧭 Orchestrateur IA</a>
            <a href="https://lucidoo.fr" target="_blank" rel="noopener noreferrer"
              style={{ background: '#7C3AED', color: '#fff', fontSize: 12 * scale, fontWeight: 700, padding: '9px 18px', borderRadius: 10, textDecoration: 'none', border: '1.5px solid #6D28D9', display: 'inline-flex', alignItems: 'center', gap: 8, transition: 'all 0.18s ease' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#6D28D9'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#7C3AED'; e.currentTarget.style.transform = 'translateY(0)'; }}>
              ∞ Lucidoo
            </a>
          </div>
          <div style={{ textAlign: 'center', marginTop: 4, marginBottom: 8 }}>
            <button
              onClick={() => onNavigate('politique')}
              style={{ background: 'none', border: 'none', color: '#AAA', fontSize: 11 * scale, cursor: 'pointer', textDecoration: 'underline', fontFamily: 'DM Sans, sans-serif' }}>
              Politique de confidentialité
            </button>
          </div>
          <div style={{ color: '#BBB', fontSize: 11 * scale, marginTop: 6 }}>
            {t?.footer?.mention || 'Un projet citoyen · Open source · Données protégées'}
          </div>
        </div>
      </main>

      {/* ── WIDGET FLOTTANT (intouchable) ── */}
      <WidgetFlottant scale={scale} onNavigate={onNavigate} />

    </div>
  );
}
